import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatIDR, formatDateID, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, ArrowDownRight, ArrowUpRight, Search, Pencil, XCircle, Download } from "lucide-react";
import CurrencyInput from "@/components/CurrencyInput";
import { Checkbox } from "@/components/ui/checkbox";
import { requireStockUnlock } from "@/lib/stockLock";

const locLabel = { gudang: "Gudang", toko: "Toko" };

export default function TransactionsPage({ type }) {
  const isIn = type === "in";
  const label = isIn ? "Stok Masuk" : "Stok Keluar";
  const partyLabel = isIn ? "Vendor Penjual" : "Customer";
  const priceLabel = isIn ? "Harga Beli" : "Harga Jual";
  const accent = isIn ? "emerald" : "rose";
  const defaultLocation = isIn ? "gudang" : "toko";
  const currentYear = new Date().getFullYear();

  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [partyFilter, setPartyFilter] = useState("__all__");
  const [exporting, setExporting] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: todayISO(),
    product_id: "",
    qty: 1,
    price: 0,
    location: defaultLocation,
    party: "",
    po_number: "",
    purchase_year: currentYear,
    note: "",
  });

  // ---- Bulk selection state ------------------------------------------------
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const emptyBulk = { date: "", party: "", price: "", po_number: "", purchase_year: "", note: "" };
  const [bulkForm, setBulkForm] = useState(emptyBulk);

  const load = async () => {
    const [tx, pr] = await Promise.all([
      api.get(`/transactions?type=${type}`),
      api.get("/products"),
    ]);
    setItems(tx.data);
    setProducts(pr.data);
  };

  useEffect(() => {
    load();
    setForm({ date: todayISO(), product_id: "", qty: 1, price: 0, location: defaultLocation, party: "", po_number: "", purchase_year: currentYear, note: "" });
    setSelectedIds(new Set());
    setPartyFilter("__all__");
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unique party list (customer for out, vendor for in). Sorted alphabetically.
  const partyOptions = useMemo(() => {
    const set = new Set();
    items.forEach((t) => {
      const p = (t.party || "").trim();
      if (p) set.add(p);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "id"));
  }, [items]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const pf = partyFilter === "__all__" ? "" : partyFilter.toLowerCase();
    return items.filter((t) => {
      if (pf && (t.party || "").toLowerCase() !== pf) return false;
      if (!s) return true;
      return (
        t.product_name.toLowerCase().includes(s) ||
        t.product_code.toLowerCase().includes(s) ||
        (t.party || "").toLowerCase().includes(s)
      );
    });
  }, [items, q, partyFilter]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ type });
      if (partyFilter !== "__all__") params.set("party", partyFilter);
      const r = await api.get(`/transactions/export-xlsx?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      const safeParty = partyFilter === "__all__" ? "semua" : partyFilter.replace(/[^A-Za-z0-9_-]+/g, "_");
      a.href = url;
      a.download = `transaksi-${type}-${safeParty}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("File Excel diunduh");
    } catch (err) {
      toast.error("Gagal export Excel");
    } finally {
      setExporting(false);
    }
  };

  const onPickProduct = (product_id) => {
    const p = products.find((x) => x.id === product_id);
    setForm((f) => ({
      ...f,
      product_id,
      price: isIn ? p?.purchase_price || 0 : p?.sell_price || 0,
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.product_id) {
      toast.error("Pilih barang dulu");
      return;
    }
    if (!(await requireStockUnlock())) return;
    try {
      await api.post("/transactions", {
        type,
        date: form.date,
        product_id: form.product_id,
        qty: Number(form.qty) || 0,
        price: Number(form.price) || 0,
        location: form.location,
        party: form.party,
        po_number: form.po_number,
        purchase_year: isIn ? Number(form.purchase_year) || null : null,
        note: form.note,
      });
      toast.success(`${label} dicatat`);
      setOpen(false);
      setForm({ date: todayISO(), product_id: "", qty: 1, price: 0, location: defaultLocation, party: "", po_number: "", purchase_year: currentYear, note: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Hapus transaksi ini? Stok akan dikembalikan.`)) return;
    try {
      await api.delete(`/transactions/${t.id}`);
      toast.success("Transaksi dihapus");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus");
    }
  };

  // ---- Bulk selection & actions -------------------------------------------
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allFilteredIds = useMemo(() => filtered.map((t) => t.id), [filtered]);
  const allSelectedInView =
    allFilteredIds.length > 0 && allFilteredIds.every((id) => selectedIds.has(id));
  const toggleAllInView = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectedInView) allFilteredIds.forEach((id) => next.delete(id));
      else allFilteredIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Hapus ${selectedIds.size} transaksi terpilih? Stok masing-masing akan dikembalikan.`)) return;
    if (!(await requireStockUnlock())) return;
    try {
      await Promise.all(Array.from(selectedIds).map((id) => api.delete(`/transactions/${id}`)));
      toast.success(`${selectedIds.size} transaksi dihapus`);
      clearSelection();
      load();
    } catch (err) {
      toast.error("Sebagian gagal dihapus");
      load();
    }
  };

  const openBulkEdit = () => {
    setBulkForm(emptyBulk);
    setBulkOpen(true);
  };

  const runBulkEdit = async (e) => {
    e.preventDefault();
    const patch = {};
    if (bulkForm.date) patch.date = bulkForm.date;
    if (bulkForm.party.trim()) patch.party = bulkForm.party.trim();
    if (bulkForm.note.trim()) patch.note = bulkForm.note.trim();
    if (String(bulkForm.price).trim() !== "") patch.price = Number(bulkForm.price) || 0;
    if (isIn) {
      if (bulkForm.po_number.trim()) patch.po_number = bulkForm.po_number.trim();
      if (String(bulkForm.purchase_year).trim() !== "") patch.purchase_year = Number(bulkForm.purchase_year) || null;
    }
    if (Object.keys(patch).length === 0) {
      toast.error("Isi minimal satu kolom untuk diubah");
      return;
    }
    if (!(await requireStockUnlock())) return;
    try {
      const r = await api.patch("/transactions/bulk-update", {
        ids: Array.from(selectedIds),
        ...patch,
      });
      toast.success(`${r.data.updated ?? 0} transaksi diperbarui`);
      setBulkOpen(false);
      clearSelection();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memperbarui");
    }
  };

  const total = filtered.reduce((s, t) => s + t.total, 0);
  const Icon = isIn ? ArrowDownRight : ArrowUpRight;

  // available stock for the selected product & location
  const selectedProduct = products.find((p) => p.id === form.product_id);
  const availableAtLocation = selectedProduct
    ? form.location === "gudang"
      ? selectedProduct.stock_gudang ?? 0
      : selectedProduct.stock_toko ?? 0
    : null;

  return (
    <div className="space-y-6" data-testid={`tx-${type}-page`}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md bg-${accent}-50 text-${accent}-700 border border-${accent}-200`}>
              <Icon className="h-4 w-4" />
            </span>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-zinc-900">{label}</h1>
          </div>
          <p className="text-sm text-zinc-500 mt-1">
            {isIn ? "Catat pembelian barang dari supplier ke Gudang / Toko." : "Catat penjualan barang dari Gudang / Toko."}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4 border-zinc-200 shadow-none rounded-md">
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Total {label}</div>
          <div className={`mt-1 text-xl font-display font-bold num ${isIn ? "text-emerald-700" : "text-rose-700"}`}>
            {formatIDR(total)}
          </div>
        </Card>
        <Card className="p-4 border-zinc-200 shadow-none rounded-md">
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Jumlah Transaksi</div>
          <div className="mt-1 text-xl font-display font-bold num">{filtered.length}</div>
        </Card>
        <Card className="p-4 border-zinc-200 shadow-none rounded-md">
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Item Terjadi</div>
          <div className="mt-1 text-xl font-display font-bold num">
            {filtered.reduce((s, t) => s + t.qty, 0)}
          </div>
        </Card>
      </div>

      <Card className="border-zinc-200 shadow-none rounded-md">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid={`tx-${type}-search`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari barang, part number, atau pihak…"
              className="pl-9 h-10 rounded-md"
            />
          </div>
          <div className="w-full sm:w-[240px]">
            <Select value={partyFilter} onValueChange={setPartyFilter}>
              <SelectTrigger
                data-testid={`tx-${type}-party-filter`}
                className="h-10 rounded-md"
              >
                <SelectValue placeholder={`Semua ${partyLabel}`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Semua {partyLabel}</SelectItem>
                {partyOptions.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            data-testid={`btn-tx-${type}-export-xlsx`}
            variant="outline"
            onClick={exportXlsx}
            disabled={exporting}
            className="h-10 rounded-md"
          >
            <Download className="h-4 w-4 mr-1.5" />
            {exporting ? "Menyiapkan…" : "Export Excel"}
          </Button>
        </div>

        {selectedIds.size > 0 && (
          <div
            className="px-4 py-3 border-b border-zinc-200 bg-zinc-900 text-white flex items-center gap-3 flex-wrap"
            data-testid={`tx-${type}-bulk-actions-bar`}
          >
            <div className="text-sm font-medium">{selectedIds.size} transaksi dipilih</div>
            <div className="flex-1" />
            <Button
              data-testid={`btn-tx-${type}-bulk-edit`}
              size="sm"
              variant="outline"
              onClick={openBulkEdit}
              className="h-8 rounded-md bg-white text-zinc-900 hover:bg-zinc-100 border-transparent"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Edit Masal
            </Button>
            <Button
              data-testid={`btn-tx-${type}-bulk-delete`}
              size="sm"
              variant="outline"
              onClick={bulkDelete}
              className="h-8 rounded-md bg-rose-600 text-white border-rose-600 hover:bg-rose-700 hover:text-white"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Hapus Terpilih
            </Button>
            <Button
              data-testid={`btn-tx-${type}-bulk-clear`}
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              className="h-8 rounded-md text-white hover:bg-white/10 hover:text-white"
            >
              <XCircle className="h-3.5 w-3.5 mr-1.5" />
              Batal
            </Button>
          </div>
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-zinc-50">
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    data-testid={`tx-${type}-bulk-select-all`}
                    checked={allSelectedInView}
                    onCheckedChange={toggleAllInView}
                    aria-label="Pilih semua"
                  />
                </TableHead>
                <TableHead className="w-[110px]">Tanggal</TableHead>
                <TableHead>Barang</TableHead>
                <TableHead className="hidden sm:table-cell">Lokasi</TableHead>
                <TableHead className="hidden md:table-cell">{partyLabel}</TableHead>
                {isIn && <TableHead className="hidden lg:table-cell">No. PO</TableHead>}
                {isIn && <TableHead className="hidden lg:table-cell text-center">Tahun</TableHead>}
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right hidden sm:table-cell">{priceLabel}</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid={`tx-${type}-table-body`}>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isIn ? 10 : 8} className="text-center py-10 text-zinc-500">
                    Belum ada transaksi.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((t) => {
                const isSelected = selectedIds.has(t.id);
                return (
                <TableRow
                  key={t.id}
                  data-state={isSelected ? "selected" : undefined}
                  className={`transition-colors duration-150 ${isSelected ? "bg-zinc-100 hover:bg-zinc-100" : "hover:bg-zinc-50"}`}
                >
                  <TableCell>
                    <Checkbox
                      data-testid={`tx-${type}-bulk-select-${t.id}`}
                      checked={isSelected}
                      onCheckedChange={() => toggleOne(t.id)}
                      aria-label={`Pilih ${t.product_name}`}
                    />
                  </TableCell>
                  <TableCell className="text-zinc-600 whitespace-nowrap">{formatDateID(t.date)}</TableCell>
                  <TableCell>
                    <div className="font-medium">{t.product_name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{t.product_code}</div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant="outline" className="border-zinc-200 bg-zinc-50 text-zinc-700 text-[10px]">
                      {locLabel[t.location] || "Gudang"}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-zinc-600">{t.party}</TableCell>
                  {isIn && (
                    <TableCell className="hidden lg:table-cell text-zinc-600 font-mono text-xs">
                      {t.po_number || <span className="text-zinc-300">—</span>}
                    </TableCell>
                  )}
                  {isIn && (
                    <TableCell className="hidden lg:table-cell text-center text-zinc-600 num">
                      {t.purchase_year || <span className="text-zinc-300">—</span>}
                    </TableCell>
                  )}
                  <TableCell className="text-right num">{t.qty}</TableCell>
                  <TableCell className="text-right num hidden sm:table-cell text-zinc-600">
                    {formatIDR(t.price)}
                  </TableCell>
                  <TableCell className={`text-right num font-semibold ${isIn ? "text-emerald-700" : "text-rose-700"}`}>
                    {formatIDR(t.total)}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`tx-${type}-dialog`}>
          <DialogHeader>
            <DialogTitle>Catat {label}</DialogTitle>
            <DialogDescription>
              {isIn ? "Pembelian akan menambah stok di lokasi yang dipilih." : "Penjualan akan mengurangi stok di lokasi yang dipilih."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <Label>Tanggal</Label>
              <Input
                data-testid="input-tx-date"
                type="date"
                required
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
            <div className="col-span-1">
              <Label>Qty</Label>
              <Input
                data-testid="input-tx-qty"
                type="number"
                inputMode="numeric"
                min={1}
                required
                value={form.qty}
                onChange={(e) => setForm({ ...form, qty: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Barang</Label>
              <Select value={form.product_id} onValueChange={onPickProduct}>
                <SelectTrigger data-testid="select-tx-product">
                  <SelectValue placeholder="Pilih barang" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} <span className="text-zinc-400 ml-2">({p.code})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1">
              <Label>Lokasi</Label>
              <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                <SelectTrigger data-testid="select-tx-location">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gudang">Gudang</SelectItem>
                  <SelectItem value="toko">Toko</SelectItem>
                </SelectContent>
              </Select>
              {availableAtLocation !== null && (
                <div className="text-[11px] text-zinc-500 mt-1">
                  Stok {locLabel[form.location]} saat ini: <span className="num font-medium">{availableAtLocation}</span>
                </div>
              )}
            </div>
            <div className="col-span-1">
              <Label>{priceLabel}</Label>
              <CurrencyInput
                data-testid="input-tx-price"
                value={form.price}
                onChange={(v) => setForm({ ...form, price: v })}
              />
            </div>
            <div className="col-span-1">
              <Label>{partyLabel}</Label>
              <Input
                data-testid="input-tx-party"
                placeholder={isIn ? "Nama vendor / supplier" : "Nama pelanggan"}
                value={form.party}
                onChange={(e) => setForm({ ...form, party: e.target.value })}
              />
            </div>
            {isIn && (
              <>
                <div className="col-span-1">
                  <Label>No. PO Pembelian</Label>
                  <Input
                    data-testid="input-tx-po"
                    placeholder="PO-2026-001"
                    value={form.po_number}
                    onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                  />
                </div>
                <div className="col-span-1">
                  <Label>Tahun Pembelian</Label>
                  <Input
                    data-testid="input-tx-year"
                    type="number"
                    inputMode="numeric"
                    min="1990"
                    max="2999"
                    value={form.purchase_year}
                    onChange={(e) => setForm({ ...form, purchase_year: e.target.value })}
                  />
                </div>
              </>
            )}
            <div className="col-span-1">
              <Label>Catatan</Label>
              <Input
                data-testid="input-tx-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </div>
            <div className="col-span-2 text-right text-sm">
              Total:{" "}
              <span className={`font-bold num ${isIn ? "text-emerald-700" : "text-rose-700"}`} data-testid="tx-form-total">
                {formatIDR((Number(form.qty) || 0) * (Number(form.price) || 0))}
              </span>
            </div>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button
                data-testid="btn-save-tx"
                type="submit"
                className={isIn ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}
              >
                Simpan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* BULK EDIT DIALOG */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg" data-testid={`tx-${type}-bulk-edit-dialog`}>
          <DialogHeader>
            <DialogTitle>Edit Masal ({selectedIds.size} transaksi)</DialogTitle>
            <DialogDescription>
              Kosongkan kolom yang tidak ingin diubah. Qty & Lokasi tidak bisa diubah masal (memengaruhi stok).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={runBulkEdit} className="grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <Label>Tanggal</Label>
              <Input
                data-testid={`tx-${type}-bulk-input-date`}
                type="date"
                value={bulkForm.date}
                onChange={(e) => setBulkForm({ ...bulkForm, date: e.target.value })}
              />
            </div>
            <div className="col-span-1">
              <Label>{priceLabel}</Label>
              <CurrencyInput
                data-testid={`tx-${type}-bulk-input-price`}
                value={bulkForm.price === "" ? "" : Number(bulkForm.price)}
                onChange={(v) => setBulkForm({ ...bulkForm, price: v })}
                placeholder="Kosong = tidak diubah"
              />
              <p className="text-[11px] text-zinc-500 mt-1">Total tiap baris otomatis dihitung ulang (qty × harga baru).</p>
            </div>
            <div className="col-span-2">
              <Label>{partyLabel}</Label>
              <Input
                data-testid={`tx-${type}-bulk-input-party`}
                placeholder="Kosong = tidak diubah"
                value={bulkForm.party}
                onChange={(e) => setBulkForm({ ...bulkForm, party: e.target.value })}
              />
            </div>
            {isIn && (
              <>
                <div className="col-span-1">
                  <Label>No. PO Pembelian</Label>
                  <Input
                    data-testid={`tx-${type}-bulk-input-po`}
                    placeholder="Kosong = tidak diubah"
                    value={bulkForm.po_number}
                    onChange={(e) => setBulkForm({ ...bulkForm, po_number: e.target.value })}
                  />
                </div>
                <div className="col-span-1">
                  <Label>Tahun Pembelian</Label>
                  <Input
                    data-testid={`tx-${type}-bulk-input-year`}
                    type="number"
                    inputMode="numeric"
                    min="1990"
                    max="2999"
                    placeholder="Kosong = tidak diubah"
                    value={bulkForm.purchase_year}
                    onChange={(e) => setBulkForm({ ...bulkForm, purchase_year: e.target.value })}
                  />
                </div>
              </>
            )}
            <div className="col-span-2">
              <Label>Catatan</Label>
              <Input
                data-testid={`tx-${type}-bulk-input-note`}
                placeholder="Kosong = tidak diubah"
                value={bulkForm.note}
                onChange={(e) => setBulkForm({ ...bulkForm, note: e.target.value })}
              />
            </div>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => setBulkOpen(false)}>Batal</Button>
              <Button
                data-testid={`btn-tx-${type}-bulk-save`}
                type="submit"
                className="bg-zinc-900 hover:bg-zinc-800"
              >
                Terapkan ke {selectedIds.size} transaksi
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
